import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import lodash from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RequirementsProps } from '../../../tools/context';
import { useTranslation } from '../../../tools/i18n';
import { coordinateGetter } from '../../../tools/sortable-dnd-kit/multipleContainersKeyboardCoordinates';
import { Button } from '../../Button';
import { Picto, pictos } from '../../Picto';
import { TravelPlanerResultsContext } from '../TravelPlanerResultsContext';
import PopupDayContainer from './PopupDayContainer';
import PopupItemContainer from './PopupItemContainer';

interface EditJourneyProps {
  picto?: keyof typeof pictos;
  withLabel?: boolean;
  requirement?: RequirementsProps;
}

export type Data = {
  [key: string]: {
    key: string;
    value: any;
  }[];
};
export type SelectedActivities = {
  [key: string]: {
    [key: string]: number;
  };
};

export type Day = {
  id: UniqueIdentifier;
};
export type Item = {
  id: UniqueIdentifier;
  dayId: string;
  data: any;
};

const updatePrice = async (body) => {
  let apiUrl = null;
  apiUrl = 'https://clients.wearegenial.com/api/v1/travel/huttopia/price';
  // apiUrl = 'http://localhost:8000/api/v1/travel/huttopia/price';
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      const data = await response.json();
      return data || null;
    }
    return null;
  } catch (e) {
    console.error('error:', e);
    return null;
  }
};

export default function EditJourney({ picto = 'edit', withLabel, requirement }: EditJourneyProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const {
    day,
    formattedData,
    selectedActivities,
    updateTrip,
    setShowAllSites,
    setShowMobileMap,
    inputs,
  } = useContext(TravelPlanerResultsContext);

  const [dataCopy, setDataCopy] = useState<Data>({});
  const [selectedActivitiesCopy, setSelectedActivitiesCopy] = useState<SelectedActivities>({});
  const [initialDays, setInitialDays] = useState<Day[]>([]);
  const [days, setDays] = useState<Day[]>([]);
  const [initialItems, setInitialItems] = useState<Item[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [activeDay, setActiveDay] = useState<Day>(null);
  const [activeItem, setActiveItem] = useState<Item>(null);
  const [hoveredDay, setHoveredDay] = useState<UniqueIdentifier>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const isNight = formattedData?.[day]?.some((d: any) => d.key?.startsWith('night'));

  const daysId = useMemo(() => days.map((day) => day.id), [days]);

  // Get the first night item for each day
  const nightItems = useMemo(() => {
    const result = {};
    if (isNight) {
      days.forEach((day) => {
        const nightItem = items.find(
          (item) => item.dayId === day.id && item.id.toString().startsWith('night_')
        );
        if (nightItem) {
          result[day.id] = nightItem;
        }
      });
    }
    return result;
  }, [days, items, isNight]);

  useEffect(() => {
    const newData = JSON.parse(JSON.stringify(formattedData));
    const newSA = JSON.parse(JSON.stringify(selectedActivities || {}));
    const dataKeys = Object.keys(newData);
    dataKeys?.forEach((day: any) => {
      if (!newSA[day]) newSA[day] = {};
      newData[day] = newData[day].map((item: any) => {
        let randomKey = Math.random().toString(36).substring(7);
        if (item.key?.startsWith('added_')) randomKey = 'added_' + randomKey;
        if (item.key?.startsWith('night')) randomKey = 'night_' + randomKey;
        newSA[day][randomKey] = newSA[day][item.key] ?? 0;
        delete newSA[day][item.key];
        return { ...item, key: randomKey };
      });
    });

    const initialDays = dataKeys.map((id: string) => ({ id }));
    setDays(initialDays);
    setInitialDays(initialDays);

    const initialItems = dataKeys.reduce((acc: Item[], dayId: string) => {
      return acc.concat(
        newData[dayId].map((item: any) => ({
          id: item.key,
          dayId,
          data: newData[dayId].find((i: any) => i.key === item.key)?.value[newSA[dayId][item.key]],
        }))
      );
    }, []);
    setItems(initialItems);
    setInitialItems(initialItems);

    setSelectedActivitiesCopy(newSA);
    setDataCopy(newData);
  }, [formattedData, selectedActivities]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  const handleDeleteDay = (id: string) => {
    setDays(days.filter((d) => d.id !== id));
  };

  const handleDeleteItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const handleApply = async () => {
    const newFormattedData = {};
    const newSelectedActivities = {};
    days.forEach((day) => {
      const dayId = day.id;
      const dayItems = isNight
        ? [nightItems[dayId]].filter(Boolean)
        : items.filter((item) => item.dayId === dayId);

      newFormattedData[dayId] = dayItems.map((item) => {
        const allItems = Object.keys(dataCopy).reduce((acc, key) => acc.concat(dataCopy[key]), []);
        return allItems.find((i) => i.key === item.id);
      });

      newSelectedActivities[dayId] = dayItems.reduce((acc, item) => {
        const allItems = Object.keys(selectedActivitiesCopy)
          .reduce((acc, key) => acc.concat(selectedActivitiesCopy[key]), [])
          .reduce((acc, item) => ({ ...acc, ...item }), {});
        acc[item.id] = allItems[item.id];
        return acc;
      }, {});
    });

    if (requirement?.config?.key === 'huttopia') {
      const tripsHaveDatesData = Object.values(newFormattedData)
        .flatMap((dayData: any) => dayData[0])
        .every(
          (t) =>
            typeof t.value[0].startDate !== 'undefined' &&
            typeof t.value[0].nightsPerSpot !== 'undefined'
        );
      if (tripsHaveDatesData && typeof inputs?.periodsOrDates?.startDate !== 'undefined') {
        let lastEndDate = new Date(inputs?.periodsOrDates?.startDate);

        // First, set all dates
        const dayDataArray = Object.values(newFormattedData);
        for (const dayData of dayDataArray) {
          dayData[0].value[0].startDate = lastEndDate.toISOString().split('T')[0];
          lastEndDate = new Date(
            lastEndDate.setDate(lastEndDate.getDate() + dayData[0].value[0].nightsPerSpot)
          );
          dayData[0].value[0].endDate = lastEndDate.toISOString().split('T')[0];
        }

        // Then, update all prices in parallel
        await Promise.all(
          dayDataArray.map(async (dayData) => {
            const newPrice = await updatePrice({
              environment: requirement.environment,
              spotId: dayData[0].value[0].id,
              startDate: dayData[0].value[0].startDate,
              endDate: dayData[0].value[0].endDate,
              adults: inputs.travelerProfile.adultCount,
              children: inputs.travelerProfile.childCount,
              equipment: inputs.travelerProfile.equipment,
            });

            // Update the price in the data structure if we got a valid price
            if (newPrice !== null) {
              dayData[0].value[0].price = newPrice;
            }

            return dayData;
          })
        );
      }
    }
    setInitialDays(days);
    setInitialItems(items);
    updateTrip(newFormattedData, newSelectedActivities);
    setIsOpen(false);
  };

  const onDragStart = (e: DragStartEvent) => {
    if (e.active.data.current.type === 'day') setActiveDay(e.active.data.current.day);
    if (e.active.data.current.type === 'item') setActiveItem(e.active.data.current.item);
  };

  const onDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    if (!active || !over) return;

    const activeType = active.data.current.type;
    const overType = over.data.current.type;

    // Set hovered day
    if (activeType === 'item') {
      setItems((p) => {
        const activeIndex = p.findIndex((item) => item.id === active.id);
        const overIndex = overType === 'day' ? 0 : p.findIndex((item) => item.id === over.id);
        const overDay = overType === 'day' ? over.id : p[overIndex].dayId;
        const isNightItem = active.id.toString().startsWith('night_');
        const currentDay = p[activeIndex].dayId;

        if (isNightItem) {
          // When moving a night item, move all items from the same day to the target day
          return p.map((item) => {
            if (item.id === active.id || item.dayId === currentDay) {
              return { ...item, dayId: String(overDay) };
            }
            return item;
          });
        } else {
          // Regular item move behavior
          p[activeIndex].dayId = String(overDay);
          return arrayMove(p, activeIndex, overIndex);
        }
      });

      if (overType === 'day') setHoveredDay(over.id);
      else if (overType === 'item') setHoveredDay(over.data.current.item.dayId);
    }
  };

  const onDragEnd = (e: DragEndEvent) => {
    setActiveItem(null);
    setActiveDay(null);
    setHoveredDay(null);

    const { active, over } = e;
    if (!active || !over) return;

    if (active.data.current.type === 'day') {
      if (!e.over) return;
      setDays((p) => {
        const activeIndex = p.findIndex((day) => day.id === active.id);
        const overIndex = p.findIndex((day) => day.id === over.id);

        return arrayMove(p, activeIndex, overIndex);
      });
    }
  };

  const sensorsOptions = {
    activationConstraint: {
      distance: 8,
      tolerance: 5,
      delay: 0,
    },
  };
  const sensors = useSensors(
    useSensor(PointerSensor, sensorsOptions),
    useSensor(MouseSensor, sensorsOptions),
    useSensor(TouchSensor, sensorsOptions),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    })
  );

  const handleOpen = () => {
    setIsOpen(true);
    // Center the popup in the viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popupWidth = 600; // Approximate width of the popup
    const popupHeight = 80 / 100; // Approximate height of the popup
    console.log(
      'viewportWidth - viewportHeight * popupHeight) / 2',
      (viewportWidth - viewportHeight * popupHeight) / 2
    );
    setPosition({
      x: Math.max(0, (viewportWidth - popupWidth) / 2),
      y: Math.max(0, (viewportHeight - viewportHeight * popupHeight) / 2),
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Don't start dragging if the click is on an interactive element or DnD item
    const isInteractiveElement =
      target.closest('button') ||
      target.closest('input') ||
      target.closest('a') ||
      target.closest('.PopupDay') ||
      target.closest('.PopupDay dragging') ||
      target.closest('.PopupDayContainer') ||
      target.closest('.PopupItemContainer');

    if (!isInteractiveElement) {
      setIsDragging(true);
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
      e.preventDefault(); // Prevent text selection during drag
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleReset = () => {
    setDays(initialDays);
    setItems(initialItems);
  };

  const hasEdit = !lodash.isEqual(initialDays, days) || !lodash.isEqual(initialItems, items);

  return (
    <div className={`EditJourney ${withLabel ? 'withLabel' : ''}`}>
      <button className="EditJourneyButton" onClick={handleOpen}>
        <Picto iconKey={picto} />
        {withLabel ? <span>{t('travelPlaner.Travel.EditJourney.title')}</span> : null}
      </button>

      {isOpen ? (
        <div
          className={`editJourneyPopup ${activeDay ? 'dragging-day' : ''} ${activeItem ? 'dragging-item' : ''}`}
          onClick={() => setIsOpen(false)}
        >
          <div
            ref={containerRef}
            className="editJourneyPopupContainer draggable"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleMouseDown}
            style={{
              position: 'absolute',
              left: `${position.x}px`,
              top: `${position.y}px`,
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          >
            <div className="closeButtonContainer">
              <button className="closeButton" onClick={() => setIsOpen(false)}>
                <div className="closeButtonContent">
                  <Picto iconKey="cross" width="14" height="14" />
                </div>
              </button>
            </div>
            <div className="editJourneyContentContainer">
              <div className="editJourneyPopupHeader">
                <span>{t('travelPlaner.Travel.EditJourney.title')}</span>
              </div>
              <DndContext
                sensors={sensors}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDragEnd={onDragEnd}
              >
                <div className="editJourneyPopupContent">
                  <SortableContext items={daysId}>
                    {days.map((day, index) => {
                      const dayItems = items.filter((item) => item.dayId === day.id);
                      // If isNight is true, only show night items
                      const filteredItems = isNight
                        ? dayItems.filter((item) => item.id.toString().startsWith('night_'))
                        : dayItems;

                      return (
                        <PopupDayContainer
                          key={day.id}
                          day={day}
                          dayIndex={index}
                          items={isNight ? [] : filteredItems}
                          nightItem={nightItems[day.id]}
                          isHovered={hoveredDay === day.id}
                          handleDeleteDay={handleDeleteDay}
                          handleDeleteItem={handleDeleteItem}
                          requirement={requirement}
                        />
                      );
                    })}
                  </SortableContext>
                </div>
                {createPortal(
                  <DragOverlay>
                    {activeDay ? (
                      <PopupDayContainer
                        overlay
                        day={activeDay}
                        dayIndex={days.findIndex((day) => day.id === activeDay.id)}
                        items={isNight ? [] : items.filter((item) => item.dayId === activeDay.id)}
                        nightItem={nightItems[activeDay.id]}
                        handleDeleteDay={handleDeleteDay}
                        handleDeleteItem={handleDeleteItem}
                        requirement={requirement}
                      />
                    ) : null}
                    {activeItem ? (
                      <PopupItemContainer
                        overlay
                        item={activeItem}
                        handleDeleteItem={handleDeleteItem}
                      />
                    ) : null}
                  </DragOverlay>,
                  document.querySelector('.editJourneyPopup') || document.body
                )}
              </DndContext>
            </div>
            <div className="editJourneyPopupFooter">
              <Button
                className="addStep"
                onClick={() => {
                  setShowAllSites(true);
                  setShowMobileMap(false);
                }}
              >
                <div className="iconWrapper">
                  <Picto iconKey="plus" />
                </div>
                {t('travelPlaner.Travel.addStep')}
              </Button>
              {hasEdit ? (
                <button className="resetButton" onClick={handleReset}>
                  <Picto iconKey="rotate-right" width="14" height="14" />
                </button>
              ) : null}
              <button className="applyButton" disabled={!hasEdit} onClick={handleApply}>
                {t('travelPlaner.Travel.EditJourney.apply')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
